import React from 'react';
import { Link } from 'react-router-dom';
import { CornerDownRight, MapPin, Map as MapIcon, Skull } from 'lucide-react';
import { CopyTextButton } from './CopyTextButton';
import { EventChip } from './EventChip';
import { coordLabel, isPoint, locationCopyText, mapName, mapPath, monsterName, monsterPath, spotPath } from '../../utils/worldFormat';
import type { MonsterTarget } from '../../utils/itemSources';
import { EventPositionList } from './EventPositionList';
import { useEventPositions } from '../../hooks/useEventPositions';
import { positionsFor } from '../../utils/eventPositions';
import { MonsterThumb } from './EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';
import type { WorldData } from '../../types/world';
const btn = 'inline-flex h-9 items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs font-semibold text-gray-200 whitespace-nowrap transition-colors duration-150 hover:border-[#D4AF37]/50 hover:text-white';

/** Un monstruo documentado para una fuente, con su mapa y sus coordenadas configuradas. */
export function MonsterTargetRow({
  w,
  t,
  viaNames = []
}: {
  w: WorldData;
  t: MonsterTarget;
  viaNames?: string[];
}) {
  const mon = monsterName(w, t.monsterId);
  const level = w.monsters.get(t.monsterId)?.level;
  const mapId = t.mapId ?? t.spawns[0]?.mapId;
  const map = mapId !== undefined ? mapName(w, mapId) : 'Mapa no especificado';
  const first = t.spawns[0];
  const ep = useEventPositions();
  // Solo objetivos de evento o invasión reciben posiciones de evento (mismo monstruo, mapa y evento).
  // Un drop normal sin spot permanente se queda en "Ubicación exacta no documentada".
  const eventPos = !t.spawns.length && t.eventId && ep ? positionsFor(ep.positions, t.monsterId, mapId, t.eventId) : [];
  const locationTo = t.spawns.length === 1 ? spotPath(first.id) : mapId !== undefined ? `${mapPath(mapId)}#spots` : `${monsterPath(t.monsterId)}#spots`;
  return <li className="rounded-2xl border border-white/10 bg-[#050b14]/60 p-3.5" data-id="element-1834" data-mp="3j2jepg42se2d">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-1835" data-mp="1pldlg0cf0ts3">
        <div className="flex min-w-0 gap-3" data-id="element-1836" data-mp="1ktvpjqp91jdz">
          <MonsterThumb entry={imageEntryForMonster(w, t.monsterId)} name={mon} data-id="element-1837" data-mp="1kt2on6cz1o3r" />
          <div className="min-w-0" data-id="element-1838" data-mp="1fw2eokd0417d">
          <p className="flex flex-wrap items-baseline gap-x-2" data-id="element-1839" data-mp="1fsy4958y0989">
            <span className="font-semibold text-white" data-id="element-1840" data-mp="1axsluger1cox">{mon}</span>
            {level !== undefined && <span className="text-xs text-gray-500" data-id="element-1841" data-mp="28in95torwtf5">Nv. monstruo {level}</span>}
          </p>
          <p className="mt-0.5 flex flex-wrap items-center gap-2 text-sm text-gray-300" data-id="element-1842" data-mp="23lwu99opg3vb">
            {map}
            {t.eventId && <EventChip w={w} eventId={t.eventId} data-id="element-1843" data-mp="1yjz21pybtyq8" />}
          </p>
          {t.isIndirect && <p className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-100" data-id="element-1844" data-mp="1ujnhed5yryer">
              <CornerDownRight className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-1845" data-mp="n7ergj8oz1r6" />
              {viaNames.length ? `Entrega ${viaNames.join(' o ')}, no este ítem directamente.` : 'Entrega una recompensa intermedia, no este ítem directamente.'}
            </p>}
          {t.spawns.length ? <ul className="mt-1.5 space-y-0.5" data-id="element-1846" data-mp="2yrme4c8v9hx6">
              {t.spawns.slice(0, 3).map(s => <li key={s.id} className="font-mono text-[13px] text-gray-300" data-id="element-1847" data-mp="33r8qykks8w5g">
                  {coordLabel(s) ?? 'Coordenadas no documentadas'}
                  <span className="ml-2 font-sans text-xs text-gray-500" data-id="element-1848" data-mp="3dhtmu092nmko">
                    {coordLabel(s) ? isPoint(s) ? 'punto' : 'área' : ''}
                    {s.kind === 'invasion' ? ' · invasión' : ''}
                    {s.quantity !== undefined ? ` · ${s.quantity} configurados` : ''}
                  </span>
                </li>)}
              {t.spawns.length > 3 && <li className="text-xs text-gray-500" data-id="element-1849" data-mp="1s6bovtxqz247">+{t.spawns.length - 3} spots más en este mapa</li>}
            </ul> : eventPos.length ? <EventPositionList w={w} positions={eventPos} data-id="element-1850" data-mp="1x6pnymhdf1q3" /> : <p className="mt-1.5 text-xs text-gray-400" data-id="element-1851" data-mp="11svik00svfhe">
              Ubicación exacta no documentada{t.eventId ? ' · disponible durante el evento' : ''}
            </p>}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-1.5" data-id="element-1852" data-mp="1brupne5x332a">
          <Link to={monsterPath(t.monsterId)} className={btn} data-id="element-1853" data-mp="16t9o9bexidc9">
            <Skull className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1854" data-mp="j3ymprpr8ex2" />
            Ver monstruo
          </Link>
          {mapId !== undefined && <Link to={mapPath(mapId)} className={btn} data-id="element-1855" data-mp="e6f8grt24254">
              <MapIcon className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1856" data-mp="sti9h8gl8sx7" />
              Ver mapa
            </Link>}
          {t.spawns.length > 0 && <Link to={locationTo} className={btn} data-id="element-1857" data-mp="o176txugqt0y">
              <MapPin className="h-3.5 w-3.5 text-[#D4AF37]" aria-hidden="true" data-id="element-1858" data-mp="cfmttr11gz9" />
              Ver ubicación
            </Link>}
          {first && coordLabel(first) && <CopyTextButton iconOnly text={locationCopyText(mapName(w, first.mapId), mon, first)} label="Copiar ubicación" data-id="element-1859" data-mp="271zovyzbjmvy" />}
        </div>
      </div>
    </li>;
}